import { Router } from "@frui.ts/screens";
import { BusyWatcher } from "@frui.ts/screens/dist";
import type ScreenBase from "@frui.ts/screens/dist/structure/screenBase";
import { Modules } from "config";
import { computed, observable, reaction } from "mobx";
import EventBus from "services/eventBus";
import LocalizationService from "services/localizationService";
import SecurityService from "services/securityService";
import NotificationService from "services/notificationService";
import type OutgoingPayGateReversalPayment from "entities/outgoingPayGateReversalPayment";
import OutgoingPayGateReversalPaymentsRequest, {
  OutgoingPayGateReversalFiltersValidationRules,
} from "entities/outgoingPayGateReversalPaymentsRequest";
import OutgoingPaygateReversalRepository from "repositories/outgoingPaygateReversalRepository";
import emptyListResponse from "../../../utils/emptyListResponse";
import { isFilterEmpty } from "../../../utils/filters";
import ListViewModel from "../../listViewModel";
import { bound } from "@frui.ts/helpers";
import { attachAutomaticValidator, hideValidationErrors } from "@frui.ts/validation";
import download from "downloadjs";
import { format } from "date-fns-tz";
import ConfirmationService from "services/confirmationService";
import EnumService from "services/enumService";
import OptionsService from "services/optionsService";

const navigationName = "reversal";

@Router.registerRoute({ name: Router.Self, route: navigationName })
export default class PaygateReversalViewModel extends ListViewModel<
  OutgoingPayGateReversalPayment,
  ScreenBase,
  ScreenBase,
  OutgoingPayGateReversalPaymentsRequest
> {
  readonly modelName = "outgoingPayGateReversalPayment";
  @observable downloadBusyWatcher = new BusyWatcher();
  protected abortController?: AbortController;

  constructor(
    protected localizationService: LocalizationService,
    protected eventBus: EventBus,
    protected securityService: SecurityService,
    protected repository: OutgoingPaygateReversalRepository,
    protected confirmationService: ConfirmationService,
    protected notificationService: NotificationService,
    protected optionsService: OptionsService,
    private enumService: EnumService
  ) {
    super();
    this.navigationName = navigationName;
  }

  onInitialize() {
    this.unsubscriptions.push(
      reaction(
        () => [
          this.filter.variableSymbol,
          this.filter.paidAmount?.from,
          this.filter.paidAmount?.to,
          this.filter.authorizedDate?.from,
          this.filter.authorizedDate?.to,
          this.filter.arn,
        ],
        () => {
          this.filter.fullTextSearchEnabled = false;
        }
      )
    );
    super.onInitialize();
  }

  @computed
  get name() {
    return this.translate("reversal");
  }

  @computed
  get allowWrite() {
    return true;
  }

  @computed
  get allowDelete() {
    return false;
  }

  @computed
  get allowReportDownload() {
    return this.securityService.allowTo("finance.reports");
  }

  @computed
  get isFilterEmpty() {
    return isFilterEmpty(this.appliedFilter);
  }

  @bound
  allowItemDetail(item: any) {
    return false;
  }

  @bound
  download() {
    const downloadAction = async () => {
      const response = await this.repository.getAllOutgoingPaygatePaymentsExport(this.filter);
      download((response as any).data, `report-${format(new Date(), "yyyyMMdd_HHmm")}.xlsx`);
    };
    this.downloadBusyWatcher.watch<void>(downloadAction());
  }

  protected createFilter() {
    return attachAutomaticValidator(new OutgoingPayGateReversalPaymentsRequest(), OutgoingPayGateReversalFiltersValidationRules);
  }

  protected async fetchData() {
    if (this.isFilterEmpty) {
      return emptyListResponse();
    }

    if (this.abortController) {
      this.abortController.abort();
      this.abortController = undefined;
    }

    const { limit, offset } = this.pagingFilter;
    this.abortController = new AbortController();

    const data = this.repository.getAllOutgoingPaygateReversalsPayments(
      { limit, offset },
      this.appliedFilter,
      this.abortController.signal
    );
    this.abortController = undefined;

    return data;
  }

  protected resetFilterValues(filter: OutgoingPayGateReversalPaymentsRequest) {
    super.resetFilterValues(filter);
    hideValidationErrors(this.filter);

    if (this.abortController) {
      this.abortController.abort();
      this.abortController = undefined;
    }
  }

  translate(code: string, size?: number): string {
    return this.localizationService.translateModule(Modules.finance, `outgoingPayments.${code}`, size);
  }
}
